import { useState } from "react";
import { Link, useLocation } from "wouter";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Search, Home, ArrowRight } from "lucide-react";
import { searchTools, categories } from "@/lib/tools-data";

const POPULAR_TOOL_IDS = ["merge-pdf", "pdf-to-word", "compress-image", "qr-generator", "password-generator", "unit-converter"];

export default function NotFound() {
  const [, navigate] = useLocation();
  usePageMeta({
    title: "Page Not Found",
    description: "This page does not exist on FreeDailyPro.",
    noindex: true,
  });
  const [query, setQuery] = useState("");

  const results = query.trim().length > 1 ? searchTools(query).slice(0, 6) : [];

  const popularTools = categories
    .flatMap(c => c.tools)
    .filter(t => POPULAR_TOOL_IDS.includes(t.id));

  return (
    <div className="container max-w-2xl mx-auto py-16 px-4 text-center">
      <h1 className="text-3xl font-bold mb-2">Hmm, we couldn't find that page</h1>
      <p className="text-muted-foreground mb-8">
        The link might be mistyped or outdated — but the tool you're after is probably still here. Try searching:
      </p>

      <div className="relative max-w-md mx-auto mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary" />
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search for a tool..."
          autoFocus
          className="w-full h-12 pl-12 pr-4 text-base rounded-xl border border-border/80 bg-background shadow-sm focus:border-primary/40 focus:outline-none transition-all"
        />
      </div>

      {results.length > 0 && (
        <div className="max-w-md mx-auto mb-8 space-y-2 text-left">
          {results.map(tool => (
            <Link key={tool.id} href={`/tool/${tool.id}`}>
              <div className="flex items-center justify-between p-3 rounded-lg border border-border bg-card hover:border-primary/30 transition-colors cursor-pointer group">
                <span className="text-sm font-medium">{tool.name}</span>
                <ArrowRight className="h-4 w-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </Link>
          ))}
        </div>
      )}

      {results.length === 0 && (
        <div className="max-w-md mx-auto mb-8">
          <p className="text-xs text-muted-foreground mb-3">Or jump to a popular tool:</p>
          <div className="flex flex-wrap gap-2 justify-center">
            {popularTools.map(tool => (
              <Link key={tool.id} href={`/tool/${tool.id}`}>
                <span className="text-xs px-3 py-1.5 rounded-full border border-border bg-card hover:border-primary/30 transition-colors cursor-pointer">
                  {tool.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <Button variant="outline" onClick={() => navigate("/")}>
        <Home className="w-4 h-4 mr-2" />
        Go to Homepage
      </Button>
    </div>
  );
}
